// ============================================================================
// SIMULADOR DE COMISIONES TRACKER ON - VERSIÓN MATERIAL-UI
// ============================================================================
// 
// ⚠️ ESTE CÓDIGO ES PARA TU PROYECTO LOCAL
// No funcionará en Claude Artifacts porque Material-UI no está disponible aquí
// 
// INSTALACIÓN REQUERIDA:
// npm install @mui/material @emotion/react @emotion/styled recharts
// o
// yarn add @mui/material @emotion/react @emotion/styled recharts
//
// INSTRUCCIONES:
// 1. Copia este código completo
// 2. Crea un archivo: src/components/AnalisisComisiones.tsx (o .jsx)
// 3. Pega el código en ese archivo
// 4. Importa en tu App: import AnalisisComisionesMUI from './components/AnalisisComisiones';
// 5. Usa el componente: <AnalisisComisionesMUI />
// ============================================================================

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Divider,
  Slider,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack
} from '@mui/material';
import {
  AttachMoney,
  Warning,
  CheckCircle,
  Person,
  AccountBalance,
  Info
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const AnalisisComisionesMUI = () => {
  // ========== ESTADOS ==========
  const [comisionPersona2COP, setComisionPersona2COP] = useState(20000000);
  const [pagoPersona1Mensual, setPagoPersona1Mensual] = useState(700);
  const [mesesPersona1, setMesesPersona1] = useState(12);
  const [tasaCambio, setTasaCambio] = useState(4300);
  const [ventaTotal, setVentaTotal] = useState(35000);
  const [deduccionesUSD, setDeduccionesUSD] = useState(0);

  // ========== CÁLCULOS ==========
  const baseGravable = ventaTotal - deduccionesUSD;
  const tasaImpuesto = 0.265; // 26.5% promedio
  const impuestoEstimado = baseGravable * tasaImpuesto;
  const netoAntesComisiones = ventaTotal - impuestoEstimado;
  const ahorroTributario = deduccionesUSD * tasaImpuesto;

  const comisionPersona2USD = comisionPersona2COP / tasaCambio;
  const costoPersona1Total = pagoPersona1Mensual * mesesPersona1;
  const totalComisionesUSD = comisionPersona2USD + costoPersona1Total;
  const netoFinal = netoAntesComisiones - totalComisionesUSD;
  const porcentajeComisiones = (totalComisionesUSD / ventaTotal) * 100;
  const porcentajeNetoReal = (netoFinal / ventaTotal) * 100;

  // ========== FUNCIONES DE FORMATO ==========
  const formatCOP = (num) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(num);

  const formatUSD = (num) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(num);

  const getSeverity = () => {
    if (porcentajeComisiones < 20) return 'success';
    if (porcentajeComisiones < 30) return 'warning';
    return 'error';
  };

  const getSeverityText = () => {
    if (porcentajeComisiones < 20) return '✅ Razonable';
    if (porcentajeComisiones < 30) return '⚠️ Alto';
    return '🚨 Muy Alto';
  };

  // ========== DATOS PARA GRÁFICOS ==========
  const distribucionData = [
    { name: 'Tu Neto', value: netoFinal, fill: '#4caf50' },
    { name: 'Persona 2', value: comisionPersona2USD, fill: '#ff9800' },
    { name: 'Persona 1', value: costoPersona1Total, fill: '#2196f3' },
    { name: 'Impuestos', value: impuestoEstimado, fill: '#f44336' }
  ].filter((item) => item.value > 0);

  const escenariosData = [
    {
      escenario: 'Actual',
      persona2: comisionPersona2USD,
      persona1: costoPersona1Total,
      tuNeto: netoFinal
    },
    {
      escenario: 'Ideal',
      persona2: ventaTotal * 0.1,
      persona1: 6000,
      tuNeto: netoAntesComisiones - ventaTotal * 0.1 - 6000
    }
  ];

  // ========== RENDER ==========
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="xl">
        
        {/* ========== HEADER ========== */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            💰 Simulador de Comisiones
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Tracker ON - Ajusta todos los valores para encontrar el equilibrio ideal
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Chip label="✏️ Venta editable" color="primary" size="small" />
            <Chip label="✏️ Tasa editable" color="secondary" size="small" />
            <Chip label="✏️ Deducciones editables" color="success" size="small" />
            <Chip label="📊 Recálculo en tiempo real" color="info" size="small" />
          </Stack>
        </Box>

        {/* ========== PANEL DE CONTROLES PRINCIPALES ========== */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, background: 'linear-gradient(to right, #e3f2fd, #f3e5f5)' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            🎛️ Ajusta los Valores
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Valor Total de Venta */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                💵 Valor Total de Venta (USD)
              </Typography>
              <TextField
                type="number"
                fullWidth
                value={ventaTotal}
                onChange={(e) => setVentaTotal(Number(e.target.value))}
                InputProps={{
                  inputProps: { min: 10000, max: 100000, step: 1000 }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'primary.main'
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                = {formatCOP(ventaTotal * tasaCambio)}
              </Typography>
            </Grid>

            {/* Tasa de Cambio */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                💱 Tasa de Cambio (COP/USD)
              </Typography>
              <TextField
                type="number"
                fullWidth
                value={tasaCambio}
                onChange={(e) => setTasaCambio(Number(e.target.value))}
                InputProps={{
                  inputProps: { min: 3500, max: 5000, step: 10 }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'secondary.main'
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                1 USD = {formatCOP(tasaCambio)}
              </Typography>
            </Grid>

            {/* Deducciones */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  📉 Deducciones Legales (USD)
                </Typography>
                <Chip label="Art. 107 ET" size="small" color="success" />
              </Box>
              <TextField
                type="number"
                fullWidth
                value={deduccionesUSD}
                onChange={(e) => setDeduccionesUSD(Number(e.target.value))}
                InputProps={{
                  inputProps: { min: 0, max: ventaTotal * 0.5, step: 500 }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'success.main'
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Ahorro: {formatUSD(ahorroTributario)} ({formatCOP(ahorroTributario * tasaCambio)})
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button size="small" variant="outlined" onClick={() => setDeduccionesUSD(0)}>
                  $0
                </Button>
                <Button size="small" variant="outlined" onClick={() => setDeduccionesUSD(3000)}>
                  $3K
                </Button>
                <Button size="small" variant="outlined" onClick={() => setDeduccionesUSD(8000)}>
                  $8K
                </Button>
                <Button size="small" variant="outlined" onClick={() => setDeduccionesUSD(12000)}>
                  $12K
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* Info de Deducciones */}
          {deduccionesUSD > 0 && (
            <Alert severity="success" icon={<Info />} sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                📊 Impacto de Deducciones Legales
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
                    <Typography variant="caption" color="text.secondary">
                      Base Gravable
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatUSD(baseGravable)}
                    </Typography>
                    <Typography variant="caption">{formatCOP(baseGravable * tasaCambio)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'error.50' }}>
                    <Typography variant="caption" color="text.secondary">
                      Impuesto Calculado
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="error">
                      {formatUSD(impuestoEstimado)}
                    </Typography>
                    <Typography variant="caption">
                      {(tasaImpuesto * 100).toFixed(1)}% de {formatUSD(baseGravable)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
                    <Typography variant="caption" color="text.secondary">
                      Ahorro Tributario
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.main">
                      +{formatUSD(ahorroTributario)}
                    </Typography>
                    <Typography variant="caption">{formatCOP(ahorroTributario * tasaCambio)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper sx={{ p: 2, bgcolor: 'success.100' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Neto Mejorado
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="success.dark">
                      {formatUSD(netoAntesComisiones)}
                    </Typography>
                    <Typography variant="caption" color="success.main">
                      vs {formatUSD(ventaTotal - ventaTotal * tasaImpuesto)} sin deducciones
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Alert>
          )}
        </Paper>

        {/* ========== SLIDERS DE COMISIONES ========== */}
        <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            🎚️ Ajusta Comisiones y Duración
          </Typography>

          <Grid container spacing={4}>
            {/* Persona 2 */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Comisión Persona 2 (Gestor Venta)
              </Typography>
              <Slider
                value={comisionPersona2COP}
                onChange={(e, val) => setComisionPersona2COP(val)}
                min={5000000}
                max={25000000}
                step={1000000}
                valueLabelDisplay="auto"
                valueLabelFormat={(val) => formatCOP(val)}
                sx={{ mt: 2 }}
              />
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'primary.50', border: 1, borderColor: 'primary.main' }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {formatCOP(comisionPersona2COP)}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color="primary.dark">
                    {formatUSD(comisionPersona2USD)}
                  </Typography>
                  <Chip
                    label={`${((comisionPersona2USD / ventaTotal) * 100).toFixed(1)}%`}
                    color="primary"
                    size="small"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Persona 1 */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Pago Mensual Persona 1 (Rol Proyecto)
              </Typography>
              <Slider
                value={pagoPersona1Mensual}
                onChange={(e, val) => setPagoPersona1Mensual(val)}
                min={0}
                max={1500}
                step={50}
                valueLabelDisplay="auto"
                valueLabelFormat={(val) => `$${val}`}
                sx={{ mt: 2 }}
                color="success"
              />
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'success.50', border: 1, borderColor: 'success.main' }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {formatUSD(pagoPersona1Mensual)}/mes
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="success.dark">
                    {formatCOP(pagoPersona1Mensual * tasaCambio)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    × {mesesPersona1} meses
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Duración */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Duración (meses)
              </Typography>
              <Slider
                value={mesesPersona1}
                onChange={(e, val) => setMesesPersona1(val)}
                min={3}
                max={24}
                step={1}
                valueLabelDisplay="auto"
                valueLabelFormat={(val) => `${val} meses`}
                sx={{ mt: 2 }}
                color="secondary"
              />
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'secondary.50', border: 1, borderColor: 'secondary.main' }}>
                <Typography variant="h4" fontWeight="bold" color="secondary.main">
                  {mesesPersona1}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  meses
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="secondary.dark">
                    {formatUSD(costoPersona1Total)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* ========== KPIS PRINCIPALES ========== */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                color: 'white'
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Venta Total
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatUSD(ventaTotal)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {formatCOP(ventaTotal * tasaCambio)}
                </Typography>
                <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Tasa: 1 USD = {formatCOP(tasaCambio)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: 'white'
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Impuestos ({(tasaImpuesto * 100).toFixed(1)}%)
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  -{formatUSD(impuestoEstimado)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {formatCOP(impuestoEstimado * tasaCambio)}
                </Typography>
                <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {deduccionesUSD > 0
                    ? `Base: ${formatUSD(baseGravable)} (con deducciones)`
                    : `Base: ${formatUSD(ventaTotal)} (sin deducciones)`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                color: 'white'
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Comisiones
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  -{formatUSD(totalComisionesUSD)}
                </Typography>
                <Chip
                  label={`${porcentajeComisiones.toFixed(1)}% del bruto`}
                  size="small"
                  sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.3)' }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background:
                  netoFinal > 15000
                    ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                    : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: 'white'
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  💰 Tu Neto Final
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {formatUSD(netoFinal)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {formatCOP(netoFinal * tasaCambio)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ========== ALERT DE EVALUACIÓN ========== */}
        <Alert
          severity={getSeverity()}
          icon={porcentajeComisiones < 20 ? <CheckCircle /> : <Warning />}
          sx={{ mb: 3 }}
        >
          <Typography variant="h6" fontWeight="bold">
            Evaluación: {getSeverityText()}
          </Typography>
          <Typography>
            Estás destinando <strong>{porcentajeComisiones.toFixed(1)}%</strong> del valor bruto a comisiones. Tu neto
            real es <strong>{porcentajeNetoReal.toFixed(1)}%</strong> de la venta total.
          </Typography>
          {deduccionesUSD > 0 && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              💡 Las deducciones de {formatUSD(deduccionesUSD)} te ahorraron {formatUSD(ahorroTributario)} en
              impuestos, mejorando tu neto en un {((ahorroTributario / netoFinal) * 100).toFixed(1)}%
            </Typography>
          )}
        </Alert>

        {/* ========== GRÁFICOS ========== */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Distribución */}
          <Grid item xs={12} lg={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                📊 Distribución del Dinero
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={distribucionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {distribucionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatUSD(value)} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Comparación */}
          <Grid item xs={12} lg={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                📈 Actual vs. Ideal
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={escenariosData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="escenario" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatUSD(value)} />
                  <Legend />
                  <Bar dataKey="persona2" fill="#ff9800" name="Persona 2" />
                  <Bar dataKey="persona1" fill="#2196f3" name="Persona 1" />
                  <Bar dataKey="tuNeto" fill="#4caf50" name="Tu Neto" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* ========== DESGLOSE DETALLADO ========== */}
        <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            💵 Desglose Detallado
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Persona 2 */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Person />
                  <Typography variant="h6" fontWeight="bold">
                    Persona 2 (Gestor Administrativo)
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="warning.dark" gutterBottom>
                  {formatCOP(comisionPersona2COP)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {formatUSD(comisionPersona2USD)} ({((comisionPersona2USD / ventaTotal) * 100).toFixed(1)}% de la
                  venta)
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Alert severity="info" icon={<Info />}>
                  <Typography variant="caption">
                    💡 Rango justo: 8-12% = {formatUSD(ventaTotal * 0.08)}-{formatUSD(ventaTotal * 0.12)}
                    <br />
                    COP: {formatCOP(ventaTotal * 0.08 * tasaCambio)}-{formatCOP(ventaTotal * 0.12 * tasaCambio)}
                  </Typography>
                </Alert>
              </Paper>
            </Grid>

            {/* Persona 1 */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'info.50', borderLeft: 4, borderColor: 'info.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Person />
                  <Typography variant="h6" fontWeight="bold">
                    Persona 1 (Rol en Proyecto)
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="info.dark" gutterBottom>
                  {formatUSD(costoPersona1Total)} total
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {formatUSD(pagoPersona1Mensual)}/mes × {mesesPersona1} meses (
                  {((costoPersona1Total / ventaTotal) * 100).toFixed(1)}% de la venta)
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Alert severity="info" icon={<Info />}>
                  <Typography variant="caption">💡 Rango PM/Enlace: $500-1,000/mes</Typography>
                </Alert>
              </Paper>
            </Grid>
          </Grid>

          {/* Barra Visual */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Visualización del flujo:
            </Typography>

            {deduccionesUSD > 0 && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.50', border: 1, borderColor: 'success.main' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Venta bruta:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold" align="right">
                      {formatUSD(ventaTotal)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="success.dark">
                      Menos: Deducciones legales
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="success.dark" fontWeight="bold" align="right">
                      -{formatUSD(deduccionesUSD)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold">
                      Base gravable:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold" align="right">
                      {formatUSD(baseGravable)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Box
              sx={{
                display: 'flex',
                height: 48,
                borderRadius: 1,
                overflow: 'hidden',
                mb: 1
              }}
            >
              <Box
                sx={{
                  bgcolor: 'success.main',
                  width: `${(netoFinal / ventaTotal) * 100}%`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}
              >
                Neto {porcentajeNetoReal.toFixed(0)}%
              </Box>
              <Box
                sx={{
                  bgcolor: 'warning.main',
                  width: `${(comisionPersona2USD / ventaTotal) * 100}%`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}
              >
                P2
              </Box>
              <Box
                sx={{
                  bgcolor: 'info.main',
                  width: `${(costoPersona1Total / ventaTotal) * 100}%`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}
              >
                P1
              </Box>
              <Box
                sx={{
                  bgcolor: 'error.main',
                  width: `${(impuestoEstimado / ventaTotal) * 100}%`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}
              >
                Imp
              </Box>
            </Box>

            {deduccionesUSD > 0 && (
              <Typography variant="caption" color="success.main" fontWeight="bold" align="center" display="block">
                ✅ Ahorraste {formatUSD(ahorroTributario)} en impuestos gracias a las deducciones
              </Typography>
            )}
          </Paper>
        </Paper>

        {/* ========== GUÍA DE DEDUCCIONES ========== */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, borderLeft: 4, borderColor: 'success.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <AccountBalance fontSize="large" color="success" />
            <Typography variant="h5" fontWeight="bold">
              📚 Guía de Deducciones Legales (Art. 107 ET)
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Deducciones Permitidas */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'success.50' }}>
                <Typography variant="h6" fontWeight="bold" color="success.dark" gutterBottom>
                  ✅ Deducciones Comunes Permitidas
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={<strong>Costos y gastos necesarios</strong>}
                      secondary="Servicios profesionales, software, equipos, oficina (hasta 100% si son necesarios)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={<strong>Intereses de vivienda</strong>}
                      secondary="Hasta 1,200 UVT (~$54M COP) si es casa o apartamento"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={<strong>Medicina prepagada y seguros</strong>}
                      secondary="Hasta 16 UVT mensuales por persona (~$700K COP/mes)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={<strong>Dependientes económicos</strong>}
                      secondary="10% del total de ingresos por cada dependiente (máx. 32 UVT)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={<strong>Aportes pensionales voluntarios</strong>}
                      secondary="Hasta 30% del ingreso laboral o tributario del año"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Ejemplos Prácticos */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'info.50' }}>
                <Typography variant="h6" fontWeight="bold" color="info.dark" gutterBottom>
                  💡 Ejemplos Prácticos
                </Typography>
                <Stack spacing={2}>
                  <Paper sx={{ p: 2, border: 1, borderColor: 'info.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Escenario 1: Gastos Profesionales
                    </Typography>
                    <Typography variant="body2">
                      Hosting + software + contador: <strong>$3,000 USD</strong>
                      <br />
                      Ahorro tributario: ~$795 USD (26.5% de $3,000)
                    </Typography>
                  </Paper>

                  <Paper sx={{ p: 2, border: 1, borderColor: 'info.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Escenario 2: Con Dependientes
                    </Typography>
                    <Typography variant="body2">
                      2 dependientes + medicina + pensión: <strong>$8,000 USD</strong>
                      <br />
                      Ahorro tributario: ~$2,120 USD (26.5% de $8,000)
                    </Typography>
                  </Paper>

                  <Paper sx={{ p: 2, border: 1, borderColor: 'info.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Escenario 3: Máximo Legal
                    </Typography>
                    <Typography variant="body2">
                      Costos + intereses + todo permitido: <strong>$12,000 USD</strong>
                      <br />
                      Ahorro tributario: ~$3,180 USD (26.5% de $12,000)
                    </Typography>
                  </Paper>
                </Stack>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>⚠️ Importante:</strong> Consulta con tu contador para determinar tus deducciones exactas.
                    Estas deben estar respaldadas con documentos y cumplir requisitos legales.
                  </Typography>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* ========== RECOMENDACIONES ========== */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            background: 'linear-gradient(135deg, #5e35b1 0%, #673ab7 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            💡 Recomendaciones Estratégicas
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                  Persona 2 Propuesta
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCOP(Math.round(((ventaTotal * 0.1 * tasaCambio) / 1000000) * 1000000))}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  ({formatUSD(ventaTotal * 0.1)} = 10% + bono futuro)
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                  Persona 1 Propuesta
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  $500/mes × 12
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Total: {formatUSD(6000)} ({formatCOP(6000 * tasaCambio)})
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  bgcolor: 'rgba(255,255,255,0.3)',
                  border: 2,
                  borderColor: 'white'
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
                  Tu Neto Ideal
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatUSD(netoAntesComisiones - ventaTotal * 0.1 - 6000)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {formatCOP((netoAntesComisiones - ventaTotal * 0.1 - 6000) * tasaCambio)} (
                  {(((netoAntesComisiones - ventaTotal * 0.1 - 6000) / ventaTotal) * 100).toFixed(1)}% del total)
                </Typography>
                {deduccionesUSD > 0 && (
                  <>
                    <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.3)' }} />
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Incluye ahorro de {formatUSD(ahorroTributario)} por deducciones
                    </Typography>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mt: 3, bgcolor: 'rgba(255,255,255,0.2)' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              🎯 Puntos clave para negociar:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={`• Persona 2: Reducir a 10% (${formatUSD(ventaTotal * 0.1)}) + bono del 5% si trae siguiente proyecto en 6 meses`}
                  sx={{ color: 'white' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Persona 1: Definir entregables específicos. Si es solo 'estar ahí', máximo $500/mes"
                  sx={{ color: 'white' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Alternativa: Ofrecer % de futuras ventas o equity en lugar de efectivo inmediato"
                  sx={{ color: 'white' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Considera que TÚ desarrollaste el producto y asumes riesgo de mantenimiento"
                  sx={{ color: 'white' }}
                />
              </ListItem>
            </List>
          </Paper>
        </Paper>
      </Container>
    </Box>
  );
};

export default AnalisisComisionesMUI;